import { toast } from "react-toastify";
import { EditOutlined, PlusOutlined, ReloadOutlined } from "@ant-design/icons";
import { Box, IconButton } from "@mui/material";
import axios from "axios";
import { Space, Typography } from "antd";
import { useTranslation } from "react-i18next";
import { useCallback, useEffect, useMemo, useState } from "react";
import { BillingService } from "../../../api/billingService";
import {
  CancellationPolicy,
  CancellationPolicyRequest,
} from "../../../@type/billing.types";
import ActionButton from "../../../components/common/buttons/ActionButton";
import CancelButton from "../../../components/common/buttons/CancelButton";
import SelectField from "../../../components/common/fields/SelectField";
import StatusBadge from "../../../components/common/StatusBadge";
import Card from "../../../components/common/Card";
import MuiTable from "../../../components/common/MuiTable";
import { useCountries } from "../../../hooks/useCountries";
import { Column } from "../../../utils/constants";
import CancellationPolicyEditor from "./CancellationPolicyEditor";

const GLOBAL_CODE = "GLOBAL";
const COUNTRY_SELECT_WIDTH = 240;

const errorText = (error: unknown, fallback: string): string =>
  axios.isAxiosError(error) ? error.response?.data?.message ?? fallback : fallback;

const CancellationPolicyConfig = () => {
  const { t } = useTranslation();
  const [policies, setPolicies] = useState<CancellationPolicy[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState<CancellationPolicy | null>(null);
  const [newCountry, setNewCountry] = useState<string>(GLOBAL_CODE);
  const [editorOpen, setEditorOpen] = useState(false);
  const [{ data: countries }] = useCountries();

  const countryOptions = useMemo(
    () => [
      { label: t("billing.policy.global_fallback"), value: GLOBAL_CODE },
      ...countries
        .filter((country) => country.code)
        .map((country) => ({
          label: `${country.name} (${country.code})`,
          value: country.code.toUpperCase(),
        })),
    ],
    [countries, t]
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await BillingService.getCancellationPolicies();
      setPolicies(response.data.data ?? []);
    } catch (error: unknown) {
      toast.error(errorText(error, t("billing.policy.load_error")));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  const openEditor = (policy: CancellationPolicy | null) => {
    setEditing(policy);
    setEditorOpen(true);
  };

  const save = async (request: CancellationPolicyRequest) => {
    const countryCode = editing ? editing.country_code : newCountry;
    setSaving(true);
    try {
      await BillingService.upsertCancellationPolicy(countryCode, request);
      toast.success(t("billing.policy.saved"));
      setEditorOpen(false);
      await load();
    } catch (error: unknown) {
      toast.error(errorText(error, t("billing.policy.save_error")));
    } finally {
      setSaving(false);
    }
  };

  const header: Column[] = [
    {
      id: "country_name",
      label: t("billing.policy.col.country"),
      minWidth: 160,
      align: "left",
      format: (_: string, row: CancellationPolicy) => (
        <Space direction="vertical" size={0}>
          <span className="font-semibold">{row.country_name}</span>
          <Typography.Text type="secondary">{row.country_code}</Typography.Text>
        </Space>
      ),
    },
    {
      id: "tiers",
      label: t("billing.policy.col.tiers"),
      minWidth: 240,
      align: "left",
      format: (_: unknown, row: CancellationPolicy) =>
        row.tiers.map((tier) => `≥${tier.min_hours_before_start}h: ${tier.fee_percent}%`).join("  ·  "),
    },
    {
      id: "no_show_fee_percent",
      label: t("billing.policy.col.no_show"),
      minWidth: 90,
      align: "right",
      format: (value: number) => `${value}%`,
    },
    {
      id: "owner_cancel_min_hours",
      label: t("billing.policy.col.salon_cancel"),
      minWidth: 100,
      align: "center",
      format: (value: number) => `≥${value}h`,
    },
    {
      id: "is_active",
      label: t("billing.policy.col.status"),
      minWidth: 90,
      align: "center",
      format: (value: boolean) => (
        <StatusBadge label={value ? t("billing.policy.active") : t("billing.policy.inactive")} tone={value ? "success" : "error"} />
      ),
    },
    {
      id: "id",
      label: t("billing.policy.col.actions"),
      minWidth: 90,
      align: "center",
      format: (_: number, row: CancellationPolicy) => (
        <IconButton size="small" color="primary" onClick={() => openEditor(row)}>
          <EditOutlined />
        </IconButton>
      ),
    },
  ];

  return (
    <Card className="user">
      <Space direction="vertical" size={20} style={{ width: "100%" }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" gap={2}>
          <Typography.Title level={4} style={{ margin: 0 }}>
            {t("billing.policy.title")}
          </Typography.Title>
          <Space>
            <CancelButton appSize="large" startIcon={<ReloadOutlined />} onClick={load} loading={loading}>
              {t("billing.policy.reload")}
            </CancelButton>
            <Box width={COUNTRY_SELECT_WIDTH}>
              <SelectField
                options={countryOptions}
                value={newCountry}
                onChange={(value) => setNewCountry(String(value ?? GLOBAL_CODE))}
              />
            </Box>
            <ActionButton
              appSize="large"
              startIcon={<PlusOutlined />}
              disabled={policies.some((policy) => policy.country_code === newCountry)}
              onClick={() => openEditor(null)}
            >
              {t("billing.policy.add")}
            </ActionButton>
          </Space>
        </Box>
        <MuiTable header={header} data={policies} isLoading={loading} />
      </Space>
      <CancellationPolicyEditor
        open={editorOpen}
        policy={editing}
        saving={saving}
        onCancel={() => setEditorOpen(false)}
        onSave={save}
      />
    </Card>
  );
};

export default CancellationPolicyConfig;
